"use client";

import { createContext, useContext, useEffect } from "react";
import en from "./en.json";
import fr from "./fr.json";

export const SUPPORTED_LANGS = ["fr", "en"];
export const DEFAULT_LANG = "fr";
export const LANG_NAMES = { fr: "Français", en: "English" };
export const LANG_PREFIX = { fr: "", en: "/en" };
export const HTML_LANG = { fr: "fr-FR", en: "en" };
export const CANONICAL_LANGS = { fr: "fr-FR", en: "en" };

const DICTIONARIES = { en, fr };

// ─────────────────── translate function ───────────────────
// Supports dot-path keys like "nav.home" and {param} placeholders like "readCount"
export function makeT(lang) {
    const dict = DICTIONARIES[lang] || DICTIONARIES[DEFAULT_LANG];

    function getValue(path) {
        const parts = path.split(".");
        let node = dict;
        for (const part of parts) {
            if (node && typeof node === "object" && part in node) {
                node = node[part];
            } else {
                return undefined;
            }
        }
        return node;
    }

    return function t(key, params) {
        let value = getValue(key);
        // fallback: try DEFAULT_LANG
        if (value === undefined && lang !== DEFAULT_LANG) {
            const fb = getValue.call({ dict: DICTIONARIES[DEFAULT_LANG] }, key);
            // simplified fallback using same logic with default dict
            value = (() => {
                const parts = key.split(".");
                let n = DICTIONARIES[DEFAULT_LANG];
                for (const p of parts) {
                    if (n && typeof n === "object" && p in n) n = n[p];
                    else return undefined;
                }
                return n;
            })();
        }
        // ultimate fallback: return the key itself so we can see missing translations
        if (value === undefined || value === null) {
            if (typeof console !== "undefined" && process.env.NODE_ENV !== "production") {
                // eslint-disable-next-line no-console
                console.warn(`[i18n] Missing translation for "${key}" (lang=${lang})`);
            }
            return key;
        }
        // Parameter substitution
        if (typeof value === "string" && params && typeof params === "object") {
            return value.replace(/\{(\w+)\}/g, (_, name) => {
                const v = params[name];
                return v !== undefined && v !== null ? String(v) : `{${name}}`;
            });
        }
        return value;
    };
}

// ─────────────────── Context (server can't use Context, so this is client-only) ───────────────────
const I18nContext = createContext(null);

export function I18nProvider({ lang, children }) {
    const t = makeT(lang);
    useEffect(() => {
        if (typeof document !== "undefined") document.documentElement.lang = HTML_LANG[lang] || lang;
    }, [lang]);
    return (
        <I18nContext.Provider value={{ lang, t, isDefaultLang: lang === DEFAULT_LANG }}>
            {children}
        </I18nContext.Provider>
    );
}

export function useI18n() {
    const ctx = useContext(I18nContext);
    if (!ctx) {
        throw new Error("useI18n must be used inside <I18nProvider>");
    }
    return ctx;
}

// Convenience: use directly in client components without hook indirection
export function useT() {
    return useI18n().t;
}

/**
 * 通用多语言字段读取 —— 后端返回 categoryNameZh / categoryNameEn / categoryNameFr，
 * 或 dealerNameZh / dealerNameEn / dealerNameFr 等，统一按当前 lang 取对应字段。
 *
 * @param {object} node      数据对象（如 category / dealer / blog 等）
 * @param {string} prefix    字段前缀，默认 "categoryName"（字段完整名 = prefix + LangSuffix）
 * @param {string} lang      当前语言 code："fr" | "en" | "zh"
 * @returns {string}         对应语言的值，fallback 链：目标语言 → 默认语言(fr) → 任何存在的字段 → ""
 */
const LANG_FIELD_SUFFIX = { fr: "Fr", en: "En", zh: "Zh" };
const FALLBACK_ORDER = ["Fr", "En", "Zh"];  // 法语优先（本站默认）

export function getLocalizedField(node, prefix = "categoryName", lang = DEFAULT_LANG) {
    if (!node || typeof node !== "object") return "";
    const targetSuffix = LANG_FIELD_SUFFIX[lang] || LANG_FIELD_SUFFIX[DEFAULT_LANG];
    const targetKey = prefix + targetSuffix;
    if (node[targetKey]) return node[targetKey];
    // fallback: fr → en → zh
    for (const s of FALLBACK_ORDER) {
        const k = prefix + s;
        if (node[k]) return node[k];
    }
    return "";
}

